/**
 * Utilitaires pour détecter et gérer les problèmes de cookies tiers avec Keycloak
 * Version simplifiée utilisant le nouveau système iframe avancé
 */

import { cookieDetectionUtils } from './advancedCookieDetector.js';

/**
 * Détecte si les cookies tiers sont bloqués - utilise le nouveau système avancé
 * @returns {Promise<boolean>} true si les cookies tiers fonctionnent, false sinon
 */
export const detectThirdPartyCookies = async () => {
    // console.log('🔍 [ThirdPartyCookies] Utilisation du système de détection avancé');
    return await cookieDetectionUtils.quickTest();
};

/**
 * Vérifie les cookies de base (localStorage, sessionStorage, etc.)
 * @returns {Object} Informations sur les capacités de stockage du navigateur
 */
export const performBasicCookieChecks = () => {
    const results = {
        firstPartyCookiesEnabled: false,
        localStorageEnabled: false,
        sessionStorageEnabled: false,
        indexedDBEnabled: false
    };

    try {
        // Test des cookies de première partie
        document.cookie = 'testCookie=test; path=/';
        results.firstPartyCookiesEnabled = document.cookie.includes('testCookie=test');
        if (results.firstPartyCookiesEnabled) {
            // Nettoyage
            document.cookie = 'testCookie=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
    } catch (e) {
        console.warn('🍪 [BasicChecks] Impossible de tester les cookies de première partie:', e);
    }

    try {
        // Test localStorage
        localStorage.setItem('test', 'test');
        results.localStorageEnabled = localStorage.getItem('test') === 'test';
        localStorage.removeItem('test');
    } catch (e) {
        console.warn('🍪 [BasicChecks] localStorage non disponible:', e);
    }

    try {
        // Test sessionStorage
        sessionStorage.setItem('test', 'test');
        results.sessionStorageEnabled = sessionStorage.getItem('test') === 'test';
        sessionStorage.removeItem('test');
    } catch (e) {
        console.warn('🍪 [BasicChecks] sessionStorage non disponible:', e);
    }

    try {
        // Test IndexedDB
        results.indexedDBEnabled = !!window.indexedDB;
    } catch (e) {
        console.warn('🍪 [BasicChecks] IndexedDB non disponible:', e);
    }

    return results;
};

/**
 * Détecte les informations du navigateur
 * @returns {Object} Informations du navigateur
 */
export const getBrowserInfo = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const browserInfo = {
        name: 'Unknown',
        version: 'Unknown',
        engine: 'Unknown'
    };

    // Détection du navigateur
    if (userAgent.includes('firefox')) {
        browserInfo.name = 'Firefox';
        const version = userAgent.match(/firefox\/(\d+)/);
        browserInfo.version = version ? version[1] : 'Unknown';
        browserInfo.engine = 'Gecko';
    } else if (userAgent.includes('chrome')) {
        if (userAgent.includes('edg')) {
            browserInfo.name = 'Edge';
            const version = userAgent.match(/edg\/(\d+)/);
            browserInfo.version = version ? version[1] : 'Unknown';
        } else if (userAgent.includes('brave')) {
            browserInfo.name = 'Brave';
            browserInfo.version = 'Unknown'; // Brave masque sa version
        } else {
            browserInfo.name = 'Chrome';
            const version = userAgent.match(/chrome\/(\d+)/);
            browserInfo.version = version ? version[1] : 'Unknown';
        }
        browserInfo.engine = 'Blink';
    } else if (userAgent.includes('safari')) {
        browserInfo.name = 'Safari';
        const version = userAgent.match(/version\/(\d+)/);
        browserInfo.version = version ? version[1] : 'Unknown';
        browserInfo.engine = 'WebKit';
    }

    return browserInfo;
};

/**
 * Effectue un diagnostic complet des cookies et du stockage
 * @returns {Promise<Object>} Diagnostic complet
 */
export const performCookieDiagnostic = async () => {
    // console.log('🔧 [CookieDiagnostic] Début du diagnostic complet');
    
    const startTime = Date.now();
    
    // Tests de base
    const basicChecks = performBasicCookieChecks();
    const browserInfo = getBrowserInfo();
    
    // Test des cookies tiers avec le nouveau système
    const thirdPartyCookiesEnabled = await detectThirdPartyCookies();
    
    const diagnostic = {
        ...basicChecks,
        thirdPartyCookiesEnabled,
        browserInfo,
        timestamp: Date.now(),
        testDuration: Date.now() - startTime,
        userAgent: navigator.userAgent,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        language: navigator.language
    };

    console.log('🔧 [CookieDiagnostic] Diagnostic terminé:', diagnostic);
    return diagnostic;
};

// Export des anciennes fonctions pour compatibilité (maintenant simplifiées)
export const checkKeycloakCookies = () => {
    // console.log('🔍 [DeprecatedFunction] checkKeycloakCookies() est déprécié, utilisez performCookieDiagnostic()');
    return performBasicCookieChecks();
};

export const detectThirdPartyCookiesBlocked = async () => {
    // console.log('🔍 [DeprecatedFunction] detectThirdPartyCookiesBlocked() est déprécié, utilisez detectThirdPartyCookies()');
    const result = await detectThirdPartyCookies();
    return !result; // Inverse car cette fonction retournait true si bloqué
};